import "@/App.css";
import "@/i18n"; // initialize react-i18next before any component mounts
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { LandingContentProvider } from "@/context/LandingContentContext";
import { PermissionsProvider } from "@/context/PermissionsContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsConditions from "@/pages/legal/TermsConditions";
import Dashboard from "@/pages/Dashboard";
import Wallet from "@/pages/Wallet";
import WalletCurrencies from "@/pages/WalletCurrencies";
import SendExternalTransfer from "@/pages/SendExternalTransfer";
import FundingHistory from "@/pages/FundingHistory";
import SendTopup from "@/pages/SendTopup";
import GiftCards from "@/pages/GiftCards";
import PayBills from "@/pages/PayBills";
import MyAgents from "@/pages/MyAgents";
import MasterAgents from "@/pages/MasterAgents";
import TransferFunds from "@/pages/TransferFunds";
import Reports from "@/pages/Reports";
import ProviderManagement from "@/pages/ProviderManagement";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import MakePayment from "@/pages/MakePayment";
import PaykuReturn from "@/pages/PaykuReturn";
import SubagentTransactions from "@/pages/SubagentTransactions";
import SendCash from "@/pages/SendCash";
import CashVerification from "@/pages/CashVerification";
import ExchangeRates from "@/pages/ExchangeRates";
import AuditLog from "@/pages/AuditLog";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import TrustedDevices from "@/pages/TrustedDevices";
import PendingCodes from "@/pages/PendingCodes";
import PendingPasswordResetCodes from "@/pages/PendingPasswordResetCodes";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SetCommissions from "@/pages/SetCommissions";
import MyCommissions from "@/pages/MyCommissions";
import MasterAgentCommissions from "@/pages/MasterAgentCommissions";
import MasterCommissionsPayouts from "@/pages/MasterCommissionsPayouts";
import AdminRecovery from "@/pages/AdminRecovery";
import LandingSettings from "@/pages/LandingSettings";
import CollaboratorManagement from "@/pages/collaborators/CollaboratorManagement";
import CreateCollaborator from "@/pages/collaborators/CreateCollaborator";
import CollaboratorPermissions from "@/pages/collaborators/CollaboratorPermissions";
import CollaboratorActivity from "@/pages/collaborators/CollaboratorActivity";
import CollaboratorSessions from "@/pages/collaborators/CollaboratorSessions";
import PendingApprovals from "@/pages/collaborators/PendingApprovals";
import ComplianceDashboard from "@/pages/collaborators/ComplianceDashboard";
import ErrorBoundary from "@/components/ErrorBoundary";
import SmsDiagnostics from "@/pages/SmsDiagnostics";

// Swallow unrelated 3rd-party fetch failures (posthog, payment SDKs) so the
// CRA dev-mode "Uncaught runtime errors" overlay does not appear in preview.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const msg = (reason && (reason.message || reason.stack || String(reason))) || "";
    if (
      msg.includes("payku") ||
      (msg.includes("Failed to fetch") && msg.includes("posthog"))
    ) {
      event.preventDefault();
    }
  });
}

const adminOnly = ["MASTER_ADMIN"];
const adminAndMaster = ["MASTER_ADMIN", "MASTER_AGENT"];
const adminAndVerifier = ["MASTER_ADMIN", "CASH_VERIFIER"];
// Roles that share the Master Admin's platform surface. Actual per-page
// access is enforced by permission checks + backend 403s; the frontend just
// stops the silent "redirect to /dashboard" that used to strand delegated
// collaborators. Every page listed here still calls the API and will render
// an empty state / 403 if the specific permission is missing.
const adminAndCollab = ["MASTER_ADMIN", "COLLABORATOR"];
const adminMasterCollab = ["MASTER_ADMIN", "MASTER_AGENT", "COLLABORATOR"];
const adminVerifierCollab = ["MASTER_ADMIN", "CASH_VERIFIER", "COLLABORATOR"];
const allRolesInclCollab = ["MASTER_ADMIN", "MASTER_AGENT", "AGENT", "CASH_VERIFIER", "COLLABORATOR"];

function Shell({ children, roles }) {
  return (
    <ProtectedRoute roles={roles}>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrandingProvider>
        <AuthProvider>
          <PermissionsProvider>
            <BrowserRouter>
            <LandingContentProvider>
            <Toaster position="top-right" richColors closeButton />
            <InstallPWAPrompt />
            <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin-recovery" element={<AdminRecovery />} />
            <Route path="/dashboard" element={<Shell><Dashboard /></Shell>} />
            <Route path="/wallet" element={<Navigate to="/wallet/currencies" replace />} />
            <Route path="/wallet/currencies" element={<Shell><WalletCurrencies /></Shell>} />
            <Route path="/wallet/external-transfer" element={<Shell><SendExternalTransfer /></Shell>} />
            <Route path="/wallet/funding-history" element={<Shell><FundingHistory /></Shell>} />
            <Route path="/wallet/legacy" element={<Shell><Wallet /></Shell>} />
            <Route path="/send-topup" element={<Shell><SendTopup /></Shell>} />
            <Route path="/gift-cards" element={<Shell><GiftCards /></Shell>} />
            <Route path="/pay-bills" element={<Shell><PayBills /></Shell>} />
            <Route path="/send-cash" element={<Shell><SendCash /></Shell>} />
            <Route path="/cash-verification" element={<Shell roles={adminVerifierCollab}><CashVerification /></Shell>} />
            <Route path="/exchange-rates" element={<Shell roles={adminAndCollab}><ExchangeRates /></Shell>} />
            <Route path="/branding-settings" element={<Navigate to="/landing-settings" replace />} />
            <Route path="/landing-settings" element={<Shell roles={adminOnly}><LandingSettings /></Shell>} />
            <Route path="/audit-log" element={<Shell roles={allRolesInclCollab}><AuditLog /></Shell>} />
            <Route path="/analytics" element={<Shell roles={allRolesInclCollab}><AnalyticsDashboard /></Shell>} />
            <Route path="/trusted-devices" element={<Shell roles={adminAndCollab}><TrustedDevices /></Shell>} />
            <Route path="/pending-codes" element={<Shell roles={adminMasterCollab}><PendingCodes /></Shell>} />
            <Route path="/sms-diagnostics" element={<Shell roles={adminOnly}><SmsDiagnostics /></Shell>} />
            <Route path="/pending-password-reset-codes" element={<Shell roles={adminMasterCollab}><PendingPasswordResetCodes /></Shell>} />
            <Route path="/master-agents" element={<Shell roles={adminAndCollab}><MasterAgents /></Shell>} />
            <Route path="/agents" element={<Shell roles={adminMasterCollab}><MyAgents /></Shell>} />
            <Route path="/set-commissions" element={<Shell roles={adminMasterCollab}><SetCommissions /></Shell>} />
            <Route path="/my-commissions" element={<Shell><MyCommissions /></Shell>} />
            <Route path="/master-agent-commissions" element={<Shell roles={adminMasterCollab}><MasterAgentCommissions /></Shell>} />
            <Route path="/master-commissions-payouts" element={<Shell roles={adminAndCollab}><MasterCommissionsPayouts /></Shell>} />
            <Route path="/transfer-funds" element={<Shell roles={adminMasterCollab}><TransferFunds /></Shell>} />
            <Route path="/reports" element={<Shell><Reports /></Shell>} />
            <Route path="/provider" element={<Shell roles={adminOnly}><ProviderManagement /></Shell>} />
            <Route path="/settings" element={<Shell><Settings /></Shell>} />
            <Route path="/profile" element={<Shell roles={allRolesInclCollab}><Profile /></Shell>} />
            <Route path="/make-payment" element={<Shell><MakePayment /></Shell>} />
            <Route path="/payku/return" element={<PaykuReturn />} />
            <Route path="/subagent-transactions" element={<Shell roles={adminMasterCollab}><SubagentTransactions /></Shell>} />
            <Route path="/collaborators" element={<Shell roles={adminOnly}><CollaboratorManagement /></Shell>} />
            <Route path="/collaborators/create" element={<Shell roles={adminOnly}><CreateCollaborator /></Shell>} />
            <Route path="/collaborators/:id/permissions" element={<Shell roles={adminOnly}><CollaboratorPermissions /></Shell>} />
            <Route path="/collaborators/activity" element={<Shell roles={adminOnly}><CollaboratorActivity /></Shell>} />
            <Route path="/collaborators/sessions" element={<Shell roles={adminOnly}><CollaboratorSessions /></Shell>} />
            <Route path="/approvals" element={<Shell roles={adminOnly}><PendingApprovals /></Shell>} />
            <Route path="/compliance" element={<Shell roles={adminOnly}><ComplianceDashboard /></Shell>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </LandingContentProvider>
          </BrowserRouter>
          </PermissionsProvider>
        </AuthProvider>
      </BrandingProvider>
    </ErrorBoundary>
  );
}
